import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";
import { normalizePatient } from "./normalizePatient";
import {
  readPersistedContext,
  persistContext,
  clearPersistedContext,
} from "./patientContextStorage";

const PatientContext = createContext(null);

export const PatientProvider = ({ children }) => {
  const persisted = readPersistedContext();
  const [patient, setPatient] = useState(persisted.patient);
  const [encounter, setEncounter] = useState(persisted.encounter);

  const selectPatient = useCallback((raw) => {
    const next = normalizePatient(raw);
    setPatient(next);
    // Switching patient always drops the encounter. This makes "patient A's
    // prescription filed against patient B's appointment" unrepresentable.
    setEncounter(null);
    persistContext(next, null);
  }, []);

  const clearPatient = useCallback(() => {
    setPatient(null);
    setEncounter(null);
    clearPersistedContext();
  }, []);

  const startEncounter = useCallback(
    (enc) => {
      setEncounter(enc ?? null);
      persistContext(patient, enc ?? null);
    },
    [patient],
  );

  const endEncounter = useCallback(() => {
    setEncounter(null);
    persistContext(patient, null);
  }, [patient]);

  const value = useMemo(
    () => ({
      patient,
      encounter,
      patientId: patient?.patient_id ?? null,
      hasPatient: Boolean(patient?.patient_id),
      hasEncounter: Boolean(encounter?.appointmentId),
      selectPatient,
      clearPatient,
      startEncounter,
      endEncounter,
    }),
    [
      patient,
      encounter,
      selectPatient,
      clearPatient,
      startEncounter,
      endEncounter,
    ],
  );

  return (
    <PatientContext.Provider value={value}>{children}</PatientContext.Provider>
  );
};

export const usePatientContext = () => {
  const ctx = useContext(PatientContext);
  if (!ctx) {
    // Screens rendered outside the provider (e.g. login) get a safe no-op shape.
    return {
      patient: null,
      encounter: null,
      patientId: null,
      hasPatient: false,
      hasEncounter: false,
      selectPatient: () => {},
      clearPatient: () => {},
      startEncounter: () => {},
      endEncounter: () => {},
    };
  }
  return ctx;
};

export default PatientContext;
